import { Helmet } from "react-helmet-async";
import { Link } from "react-router-dom";
import { ArrowRight, Mic, Send, Calendar, BarChart3, Users, Sparkles, Shield, Smartphone } from "lucide-react";
import { MarketingNav } from "@/components/marketing/MarketingNav";
import { MarketingFooter } from "@/components/marketing/MarketingFooter";
import { ProductMock } from "@/components/marketing/ProductMock";
import { Button } from "@/components/ui/button";

const sections = [
  {
    icon: Mic, h: "Voice-to-CRM",
    p: "Hit record between calls. EchoCRM transcribes, extracts, and files the lead, names, companies, amounts, next steps.",
    bullets: ["No fields to fill", "Edit anything inline", "Works in the AI chat or directly in the lead dialog"],
  },
  {
    icon: Send, h: "Telegram-native",
    p: "Connect a personal or group Telegram bot. Add and update leads from any chat without leaving the conversation.",
    bullets: ["Per-workspace bot", "Natural-language commands", "Two-minute group invite codes"],
  },
  {
    icon: BarChart3, h: "Visual timelines",
    p: "A clean Gantt for every deal. Drag tasks across days, track milestones, export to PDF when sharing externally.",
    bullets: ["Day-snap drag and drop", "Timed events with durations", "PDF export with full calendar dates"],
  },
  {
    icon: Calendar, h: "Calendar sync",
    p: "Two-way Google Calendar sync per workspace. Timezone-aware. Renames, reschedules, and deletions propagate cleanly.",
    bullets: ["Per-workspace calendars", "Auto-pull on open", "Manual sync button when you want it"],
  },
  {
    icon: Users, h: "Workspaces & roles",
    p: "Personal and shared group workspaces with role-based permissions. Bring teammates in seconds via invite code.",
    bullets: ["Personal + group", "Role-based access", "Real-time updates"],
  },
  {
    icon: Sparkles, h: "AI assistant",
    p: "Ask the assistant to add, update, or move leads in plain language. It remembers the last 10 messages of context.",
    bullets: ["Sheet-aware navigation", "Name disambiguation", "Edits without ever creating duplicates"],
  },
  {
    icon: Shield, h: "Built secure",
    p: "Strict row-level security on every table. Role checks happen on the server, never on the client.",
    bullets: ["RLS on all tables", "Stripe-driven plan access", "Rate-limited password recovery"],
  },
  {
    icon: Smartphone, h: "Mobile-first",
    p: "Designed at 375px first. Bottom tab navigation, large tap targets, full-screen sheets on mobile, and a one-tap floating add button.",
    bullets: ["44×44 minimum tap targets", "Bottom tab bar on mobile", "Safe-area inset support"],
  },
];

const Features = () => (
  <>
    <Helmet>
      <title>Features | EchoCRM</title>
      <meta name="description" content="A close look at how EchoCRM turns voice, Telegram, and AI into a working sales pipeline." />
      <link rel="canonical" href="https://echocrm.app/features" />
    </Helmet>

    <main className="min-h-screen bg-background text-foreground">
      <MarketingNav />

      <section className="theme-glow">
        <div className="mx-auto max-w-6xl px-5 sm:px-8 lg:px-12 pt-16 sm:pt-24 pb-12 sm:pb-16">
          <div className="max-w-3xl">
            <p className="text-[12px] font-mono-accent uppercase tracking-[0.18em] text-muted-foreground mb-4">Features</p>
            <h1 className="font-display font-semibold text-[36px] sm:text-[56px] tracking-[-0.035em] leading-[1.05]">
              Every feature earns its keep.
            </h1>
            <p className="mt-6 text-[16px] sm:text-[18px] text-muted-foreground max-w-2xl">
              No bloat, no vanity metrics. Each capability exists to remove a step from your day.
            </p>
          </div>
        </div>
        <div className="mx-auto max-w-6xl px-5 sm:px-8 lg:px-12 pb-16 sm:pb-20">
          <ProductMock />
        </div>
      </section>

      {/* Mobile: accordion. Desktop: full sections */}
      <section className="hairline-t">
        <div className="mx-auto max-w-6xl px-5 sm:px-8 lg:px-12 py-12 md:py-20">
          <div className="md:hidden divide-y divide-border border-y border-border">
            {sections.map((s) => (
              <details key={s.h} className="group py-5">
                <summary className="flex items-center justify-between cursor-pointer list-none">
                  <span className="flex items-center gap-3">
                    <s.icon className="h-4 w-4 text-primary" />
                    <span className="text-[15px] font-medium">{s.h}</span>
                  </span>
                  <span className="text-muted-foreground group-open:rotate-45 transition-transform text-lg leading-none">+</span>
                </summary>
                <div className="mt-3 pl-7">
                  <p className="text-[14px] text-muted-foreground leading-[1.55]">{s.p}</p>
                  <ul className="mt-3 space-y-1.5 text-[13px] text-foreground/90">
                    {s.bullets.map((b) => (
                      <li key={b} className="flex gap-2"><span className="text-primary">·</span>{b}</li>
                    ))}
                  </ul>
                </div>
              </details>
            ))}
          </div>

          <div className="hidden md:block divide-y divide-border border-y border-border">
            {sections.map((s) => (
              <div key={s.h} className="grid grid-cols-12 gap-8 py-12 lg:py-16">
                <div className="col-span-4">
                  <s.icon className="h-4 w-4 text-primary mb-3" />
                  <h2 className="font-display font-semibold text-[22px] tracking-[-0.02em]">{s.h}</h2>
                </div>
                <div className="col-span-8">
                  <p className="text-[15px] text-muted-foreground leading-[1.6] max-w-2xl">{s.p}</p>
                  <ul className="mt-4 space-y-1.5 text-[14px] text-foreground/90">
                    {s.bullets.map((b) => (
                      <li key={b} className="flex gap-2"><span className="text-primary">·</span>{b}</li>
                    ))}
                  </ul>
                </div>
              </div>
            ))}
          </div>

          <div className="mt-16 flex flex-col sm:flex-row gap-3 sm:gap-5">
            <Button size="lg" className="w-full sm:w-auto" asChild>
              <Link to="/signup">Get started <ArrowRight className="h-4 w-4" /></Link>
            </Button>
            <Link to="/pricing" className="text-[14px] text-muted-foreground hover:text-foreground inline-flex items-center justify-center sm:justify-start gap-1.5 py-2">
              See pricing <ArrowRight className="h-3.5 w-3.5" />
            </Link>
          </div>
        </div>
      </section>

      <MarketingFooter />
    </main>
  </>
);

export default Features;